import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useIntl } from 'react-intl';
import type { IIdleTimer } from 'react-idle-timer';

import styled from 'styled-components';
import { useAppDispatch, useAppSelector } from '@hooks/index';
import { ConditionalRendering } from '../common/conditional-rendering';
import { environmentSelector } from '@selectors/commonSelectors';
import { setLanguage } from '@actions/common/actionCreators';
import { useLocation } from '@hooks/useLocation';
import { exportToExcel, filterObject } from '@hooks/useTranslations';

import SessionTimerInfo from './blocks/SessionTimerInfo';
import { darculaSessionTimerStyles } from './darculaTheme';
import type { ISessionTimerConfig } from '@hooks/useSessionIdle';

const StyledContainer = styled.div`
    padding: 10px;
    border-bottom : 2px solid gray;
    position: relative;
    z-index:1001
`;

const StyledButton = styled.button<{ isSelected: boolean }>`
    padding: 3px;
    background-color: ${({ isSelected }) => (isSelected ? '#90EE90' : '')};;
    outline: none;
    border-radius: 3px;
    cursor: pointer;
    margin-right: 20px;
    color: black;
`;

const StyledOverlayToggle = styled.label`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    vertical-align: middle;
    color: black;
    font-weight: 600;
    cursor: pointer;
    user-select: none;
`;

const languages = {
    DUBUG: 'debug',
    ENGLISH: 'en',
};

const eventCodes = {
    KEY1 : 'Digit1'
};

const { overlayContainerStyle } = darculaSessionTimerStyles;

interface IDebugLabelSelectorProps {
    idleTimer?: IIdleTimer | null;
    sessionConfig?: ISessionTimerConfig;
}

export const DebugLabelSelector = ({ idleTimer, sessionConfig }: IDebugLabelSelectorProps = {}) => {
    const dispatch = useAppDispatch();
    const intl = useIntl();
    const [keys, setKeys] = useState(new Set());
    const [isDropdownVisible, setDropdownVisible] = useState(false);
    const pendingKeysRef = useRef(new Set());
    const flushScheduledRef = useRef(false);
    // In-memory only: resets to unchecked on every page load (no persistence).
    const [isSessionTimerOverlayShown, setIsSessionTimerOverlayShown] = useState(false);
    const { language, devEnvironment } = useAppSelector(environmentSelector);

    const handleSessionTimerOverlayToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
        setIsSessionTimerOverlayShown(event.target.checked);
    };
    const messages = useAppSelector(state => state.common.messages);
    const { currentLocation } = useLocation();

    useEffect(() => {
        setKeys(new Set());
    }, [currentLocation]);

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if ((event.metaKey || event.ctrlKey) && event.shiftKey && event.code === eventCodes.KEY1 && devEnvironment) {
                event.preventDefault();
                setDropdownVisible(prev => !prev);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
        // Re-bind when devEnvironment resolves (e.g. false -> true after the
        // environment API loads) so the Ctrl+Shift+1 toggle reflects the latest value.
    }, [devEnvironment]);

    useEffect(() => {
        const originalFormatMessage = intl.formatMessage;
        intl.formatMessage = (descriptor, ...args) => {
            if (descriptor?.id) {
                pendingKeysRef.current.add(descriptor.id);
                if (!flushScheduledRef.current) {
                    flushScheduledRef.current = true;
                    queueMicrotask(() => {
                        flushScheduledRef.current = false;
                        setKeys((prev) => {
                            const next = new Set(prev);
                            for (const k of pendingKeysRef.current) next.add(k);
                            pendingKeysRef.current.clear();
                            return next;
                        });
                    });
                }
            }
            return originalFormatMessage(descriptor, ...args);
        };
        return () => {
            intl.formatMessage = originalFormatMessage;
        };

    }, [intl]);

    function getTranslations() {
        const filteredObject = filterObject(messages['en'], keys);
        exportToExcel(filteredObject, currentLocation);
    }


    return (
        <>
            <ConditionalRendering isTrue={isDropdownVisible && devEnvironment} >
                <StyledContainer>
                    <StyledButton isSelected={language === languages.DUBUG} onClick={() => dispatch(setLanguage('debug'))}>
                        Show Content Labels
                    </StyledButton>
                    <StyledButton isSelected={language === languages.ENGLISH} onClick={() => dispatch(setLanguage('en'))}>
                        Show Content
                    </StyledButton >
                    <StyledButton onClick={() => getTranslations()}>
                        Download Content
                    </StyledButton>
                    <StyledOverlayToggle>
                        <input
                            type="checkbox"
                            checked={isSessionTimerOverlayShown}
                            onChange={handleSessionTimerOverlayToggle}
                            data-testid="session-timer-overlay-toggle"
                        />
                        Session Timer overlay
                    </StyledOverlayToggle>
                </StyledContainer>
            </ConditionalRendering>
            {
                devEnvironment && isDropdownVisible && isSessionTimerOverlayShown && createPortal(
                    <div style={overlayContainerStyle as React.CSSProperties}>
                        <SessionTimerInfo idleTimer={idleTimer} sessionConfig={sessionConfig} />
                    </div>,
                    document.body
                )
            }
        </>
    );
};